import { ResolvedTheme, useThemeStore } from '@/lib/store/themeStore';

// Values live ONLY in src/styles/tokens.css — this module resolves them at runtime.
export type TokenName =
  | 'background'
  | 'foreground'
  | 'card'
  | 'cardForeground'
  | 'popover'
  | 'popoverForeground'
  | 'primary'
  | 'primaryForeground'
  | 'secondary'
  | 'secondaryForeground'
  | 'muted'
  | 'mutedForeground'
  | 'accent'
  | 'accentForeground'
  | 'destructive'
  | 'destructiveForeground'
  | 'border'
  | 'input'
  | 'ring'
  | 'radius'
  | 'chart1'
  | 'chart2'
  | 'chart3'
  | 'chart4'
  | 'chart5'
  | 'sidebar'
  | 'sidebarForeground'
  | 'sidebarPrimary'
  | 'sidebarPrimaryForeground'
  | 'sidebarAccent'
  | 'sidebarAccentForeground'
  | 'sidebarBorder'
  | 'sidebarRing'
  | 'apCanvas'
  | 'apTile'
  | 'apRowHover'
  | 'apRowSelected'
  | 'apSuccess'
  | 'apWarning';

const kebab = (name: TokenName): string =>
  name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([a-zA-Z])([0-9])/g, '$1-$2')
    .toLowerCase();

/** CSS custom-property reference for a token — for JS consumers that accept `var()` strings. */
export const tokenVar = (name: TokenName): string => `var(--${kebab(name)})`;

/** Resolved value of a token under the CURRENT theme; '' when the DOM is unavailable. */
export const getToken = (name: TokenName): string => {
  try {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(`--${kebab(name)}`)
      .trim();
  } catch {
    return '';
  }
};

/** Re-run consumers (e.g. chart adapters) when the resolved theme flips. Returns unsubscribe. */
export const onThemeChange = (cb: (resolvedTheme: ResolvedTheme) => void): (() => void) =>
  useThemeStore.subscribe((state, prevState) => {
    if (state.resolvedTheme !== prevState.resolvedTheme) cb(state.resolvedTheme);
  });

// ---- JS-side sRGB blending (browser floor, THEME.md law 10) ----
// Runtime color-mix(in srgb, …) strings can't parse below Safari 16.2, so
// consumers that mix an authored (wire) color with a token blend here instead.

type SrgbChannels = { r: number; g: number; b: number };

/** Token oklch() value → gamma-encoded sRGB 0–255 (math mirrors tokens.contrast.test.ts).
 *  Exported for lib/authoredInk (J22-B03) — surfaces resolve as oklch() in
 *  modern browsers' computed-value serialization. */
export const oklchToSrgb = (value: string): SrgbChannels | null => {
  // PRODUCTION serialization accepted: lightningcss normalizes L to PERCENT
  // form (`oklch(52% .105 223.128/.1)`) in the built CSS while raw tokens.css
  // uses the number form — parse both, or the blender is dead in prod builds.
  const m = value.match(
    /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+)(%?)\s*)?\)$/
  );
  if (!m) return null;
  // Alpha-baked tokens (wash tokens, dark hairlines) have no single opaque
  // sRGB triple — fail LOUD (null → caller falls back) instead of silently
  // dropping the alpha and blending a wrong color.
  if (m[5] !== undefined) return null;
  const L = m[2] === '%' ? parseFloat(m[1]) / 100 : parseFloat(m[1]);
  const C = parseFloat(m[3]);
  const H = (parseFloat(m[4]) * Math.PI) / 180;
  const a = C * Math.cos(H);
  const b = C * Math.sin(H);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mm = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const gamma = (x: number) => {
    const c = Math.min(1, Math.max(0, x));
    const g = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
    return Math.round(g * 255);
  };
  return {
    r: gamma(4.0767416621 * l - 3.3077115913 * mm + 0.2309699292 * s),
    g: gamma(-1.2684380046 * l + 2.6097574011 * mm - 0.3413193965 * s),
    b: gamma(-0.0041960863 * l - 0.7034186147 * mm + 1.707614701 * s),
  };
};

const hexToSrgb = (hex: string): SrgbChannels | null => {
  const m = hex.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

// Resolved token → sRGB, cached per theme (getComputedStyle is not free on
// list surfaces that render hundreds of consumers).
const srgbCache = new Map<TokenName, SrgbChannels | null>();
onThemeChange(() => srgbCache.clear());

const getTokenSrgb = (name: TokenName): SrgbChannels | null => {
  if (srgbCache.has(name)) return srgbCache.get(name) ?? null;
  const parsed = oklchToSrgb(getToken(name));
  srgbCache.set(name, parsed);
  return parsed;
};

/**
 * Gamma-space sRGB lerp of an authored hex color over a token's CURRENT value
 * — the JS equivalent of `color-mix(in srgb, <hex> <ratio>, var(--token))`.
 * Returns an `rgb()` string, or null when either side is unparseable.
 *
 * ROOT-SCOPE CONTRACT: tokens resolve at `documentElement` only. Inside a
 * `.force-light` subtree (paper surfaces, V1-fallback outlet) a dark-root
 * blend would be wrong — don't add consumers there without adding an
 * element-scoped resolve first.
 */
export const blendHexOverToken = (
  hex: string,
  token: TokenName,
  ratio: number
): string | null => {
  const fg = hexToSrgb(hex);
  const bg = getTokenSrgb(token);
  if (!fg || !bg) return null;
  const mix = (f: number, b: number) => Math.round(ratio * f + (1 - ratio) * b);
  return `rgb(${mix(fg.r, bg.r)}, ${mix(fg.g, bg.g)}, ${mix(fg.b, bg.b)})`;
};
